import React, { useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Zap, Flame, ShieldCheck } from 'lucide-react';
import { useBrandConfig } from "@/features/brandDemo/BrandConfigContext.jsx";

export const DemoStepBetting = ({
    userScore,
    matchScore,
    handleScoreChange,
    calculations,
    onValidate
}) => {
    const { t } = useTranslation();
    const { brand } = useBrandConfig();
    const validateBtnRef = useRef(null);

    const isMinusDisabled = (team) => {
        const currentMatchScore = matchScore?.[team] || 0;
        return userScore[team] <= currentMatchScore;
    };

    const handleScoreClick = (team, type) => {
        handleScoreChange(team, type);
        setTimeout(() => {
            if (validateBtnRef.current) {
                validateBtnRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }, 150);
    };

    const safeTotal = calculations?.total || 0;

    const boostData = useMemo(() => {
        let percentage = Math.min((safeTotal / 1200) * 100, 100);
        if (!percentage || isNaN(percentage) || percentage < 15) percentage = 15;

        let status = "safe";
        let color = "from-[#00E573] to-[#00E573]";
        let Icon = ShieldCheck;

        if (percentage > 40 && percentage <= 75) {
            status = "bold";
            color = "from-[#00E573] to-[#F4D03F]";
            Icon = Zap;
        } else if (percentage > 75) {
            status = "legendary";
            color = "from-[#F4D03F] to-[#FF003C]";
            Icon = Flame;
        }

        return { percentage, status, color, Icon };
    }, [safeTotal]);

    return (
        <div className="mt-4 animate-fade-in pb-8 font-body max-w-sm mx-auto w-full relative z-30">

            {/* CARTE PRINCIPALE */}
            <div className="relative bg-white/30 backdrop-blur-3xl rounded-[2.5rem] p-6 shadow-[0_15px_40px_-10px_rgba(0,0,0,0.15)] border border-white/40 mb-6 flex flex-col items-center">

                <p className="text-[10px] font-black text-black/40 uppercase tracking-[0.2em] mb-6">
                    {t('holy.betting.subtitle')}
                </p>

                {/* SÉLECTEURS DE SCORE */}
                <div className="flex justify-center items-center gap-6 w-full mb-8">

                    {/* TEAM 1 */}
                    <div className="flex flex-col items-center gap-3 w-1/3">
                        <button
                            id="demo-btn-plus"
                            onClick={() => handleScoreClick('team1', 'plus')}
                            className="w-12 h-12 rounded-full bg-white shadow-md text-black flex items-center justify-center font-black text-2xl active:scale-90 transition-transform hover:bg-gray-50 border border-white/50"
                        >
                            ＋
                        </button>
                        <div className="text-[4.5rem] text-black font-heading leading-[0.8] tracking-tighter">
                            {userScore.team1}
                        </div>
                        <button
                            onClick={() => handleScoreClick('team1', 'minus')}
                            disabled={isMinusDisabled('team1')}
                            className={`w-12 h-12 rounded-full flex items-center justify-center font-black text-3xl transition-transform border border-white/50
                                ${isMinusDisabled('team1')
                                    ? 'bg-white/20 text-black/20 cursor-not-allowed shadow-none'
                                    : 'bg-white shadow-md text-black active:scale-90 hover:bg-gray-50'}`}
                        >
                            －
                        </button>
                    </div>

                    {/* SÉPARATEUR */}
                    <div className="flex flex-col items-center justify-center h-full pt-2 opacity-30">
                        <div className="w-2 h-2 rounded-full bg-black mb-2" />
                        <div className="w-2 h-2 rounded-full bg-black" />
                    </div>

                    {/* TEAM 2 */}
                    <div className="flex flex-col items-center gap-3 w-1/3">
                        <button
                            onClick={() => handleScoreClick('team2', 'plus')}
                            className="w-12 h-12 rounded-full bg-white shadow-md text-black flex items-center justify-center font-black text-2xl active:scale-90 transition-transform hover:bg-gray-50 border border-white/50"
                        >
                            ＋
                        </button>
                        <div className="text-[4.5rem] text-black font-heading leading-[0.8] tracking-tighter">
                            {userScore.team2}
                        </div>
                        <button
                            onClick={() => handleScoreClick('team2', 'minus')}
                            disabled={isMinusDisabled('team2')}
                            className={`w-12 h-12 rounded-full flex items-center justify-center font-black text-3xl transition-transform border border-white/50
                                ${isMinusDisabled('team2')
                                    ? 'bg-white/20 text-black/20 cursor-not-allowed shadow-none'
                                    : 'bg-white shadow-md text-black active:scale-90 hover:bg-gray-50'}`}
                        >
                            －
                        </button>
                    </div>
                </div>

                {/* JAUGE & POINTS */}
                <div className="w-full pt-6 border-t border-black/10">
                    <div className="flex flex-col gap-5">

                        <div className="flex flex-col gap-2">
                            <div className="flex justify-between items-end">
                                <span className="text-[10px] font-black text-black/50 uppercase tracking-[0.15em]">
                                    {t('holy.betting.risk_label')}
                                </span>
                                <div className="flex items-center gap-1">
                                    <boostData.Icon size={12} className="text-black/70" />
                                    <span className={`text-[10px] font-black uppercase tracking-widest ${boostData.percentage > 75 ? 'text-[#FF003C]' : 'text-black'}`}>
                                        {t(`holy.betting.risk_${boostData.status}`)}
                                    </span>
                                </div>
                            </div>
                            <div className="h-3 w-full bg-black/5 rounded-full overflow-hidden shadow-inner relative p-0.5">
                                <div
                                    className={`h-full rounded-full bg-gradient-to-r ${boostData.color} transition-all duration-700 ease-out`}
                                    style={{ width: `${boostData.percentage}%` }}
                                />
                            </div>
                        </div>

                        <div className="flex justify-between items-center rounded-2xl px-5 py-3">
                            <span className="text-[11px] font-black text-black/50 uppercase tracking-[0.2em]">
                                {t('holy.betting.gain_label')}
                            </span>
                            <div className="flex items-baseline gap-1">
                                <span className="text-[3.2rem] font-heading italic tracking-tighter text-black leading-none drop-shadow-sm">
                                    {safeTotal}
                                </span>
                                <span className="text-[12px] font-black text-black/50 uppercase tracking-widest">
                                    {t('holy.betting.pts')}
                                </span>
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            {/* BOUTON DE VALIDATION */}
            <button
                id="demo-btn-validate"
                ref={validateBtnRef}
                onClick={onValidate}
                className="group w-full py-5 font-heading italic text-2xl uppercase tracking-wider rounded-full shadow-lg active:translate-y-1 active:shadow-none transition-all flex items-center justify-center gap-3"
                style={{ backgroundColor: brand.colors.accent, color: brand.colors.accentText }}
            >
                {t('holy.betting.validate_btn')}
                <ChevronRight size={28} strokeWidth={3} className="group-hover:translate-x-1 transition-transform" />
            </button>
        </div>
    );
};
